import { omit, isNil } from 'lodash-es'
import { computed, inject, type ComputedRef } from 'vue'

import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import type { ThemeBorderRadius, ThemePadding } from '@canvas-builder/composables/useCanvasTheme'
import type {
  BackgroundAttachment,
  BackgroundPositionX,
  BackgroundPositionY,
  BackgroundRepeat,
  BackgroundSize,
} from '@lib/types/background'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CanvasTheme, ComponentNodeDynamicDataTableThemeMeta } from '@canvas-builder/types/canvas'
import { ComponentTypes, type ComponentNode } from '@canvas-builder/types/canvas'

export interface ComponentStyle {
  backgroundColor?: string
  backgroundAttachment?: BackgroundAttachment
  backgroundImage?: string | null
  backgroundPositionX?: BackgroundPositionX
  backgroundPositionY?: BackgroundPositionY
  backgroundRepeat?: BackgroundRepeat
  backgroundSize?: BackgroundSize
  borderRadius?: string
  height?: string
  marginBottom?: string
  padding?: string
  width?: string
}

type DataTableThemeMetaOverride = {
  headerBackgroundColor?: string
  groupHeaderBackgroundColor?: string
}

type ThemeMetaOverride = DataTableThemeMetaOverride & Record<string, any>

export function useComponentStyle(componentNode?: ComputedRef<ComponentNode | null>) {
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const { globalComponentSpacing, maintainSectionTheme, palette } = useCanvasTheme()

  const borderRadiusThemeValues = computed(() => {
    const values: ThemeBorderRadius = {
      topLeft: componentNode?.value?.style?.borderTopLeftRadius
        ? parseInt(componentNode.value.style.borderTopLeftRadius as string)
        : 0,
      topRight: componentNode?.value?.style?.borderTopRightRadius
        ? parseInt(componentNode.value.style.borderTopRightRadius as string)
        : 0,
      bottomLeft: componentNode?.value?.style?.borderBottomLeftRadius
        ? parseInt(componentNode.value.style.borderBottomLeftRadius as string)
        : 0,
      bottomRight: componentNode?.value?.style?.borderBottomRightRadius
        ? parseInt(componentNode.value.style.borderBottomRightRadius as string)
        : 0,
    }

    return values
  })

  const componentSpacingThemeValues = computed(() => {
    return componentNode?.value?.style?.marginBottom
      ? parseInt(componentNode.value.style.marginBottom as string)
      : globalComponentSpacing.value
  })

  const paddingThemeValues = computed(() => {
    const values: ThemePadding = {
      bottom: componentNode?.value?.style?.paddingBottom
        ? parseInt(componentNode.value.style.paddingBottom as string)
        : 0,
      left: componentNode?.value?.style?.paddingLeft ? parseInt(componentNode.value.style.paddingLeft as string) : 0,
      top: componentNode?.value?.style?.paddingTop ? parseInt(componentNode.value.style.paddingTop as string) : 0,
      right: componentNode?.value?.style?.paddingRight ? parseInt(componentNode.value.style.paddingRight as string) : 0,
    }

    return values
  })

  function getCssThemeMetaStyleOverrides(
    componentType: ComponentNode['type'],
    meta: ComponentNode['theme_meta'],
    sectionTheme?: CanvasTheme,
  ): Record<string, any> {
    let p = palette.value

    if (sectionTheme && sectionTheme.inherit_theme_from_parent) {
      p = palette.value
    } else if (sectionTheme && maintainSectionTheme.value) {
      p = sectionTheme.palette ?? palette.value
    }

    if (
      launchDarkly.value.enable_themes &&
      meta &&
      !isNil(meta.bg_color_palette_index) &&
      meta.bg_color_palette_index >= 0 &&
      (componentType === ComponentTypes.Grid ||
        componentType === ComponentTypes.GridItem ||
        componentType === ComponentTypes.Text ||
        componentType === ComponentTypes.DynamicDataTable ||
        componentType === ComponentTypes.Scribble)
    ) {
      const obj: Record<string, any> = {}
      if (p[meta.bg_color_palette_index]) {
        obj.backgroundColor = p[meta.bg_color_palette_index]
      }
      return obj
    }
    return {}
  }

  function getComponentSpecificThemeMetaOverrides(
    componentType: ComponentNode['type'],
    meta: ComponentNode['theme_meta'],
  ): ThemeMetaOverride {
    if (launchDarkly.value.enable_themes && componentType === ComponentTypes.DynamicDataTable) {
      const dynamicDataTableMeta = meta as ComponentNodeDynamicDataTableThemeMeta
      const obj: ThemeMetaOverride = {}
      if (
        !isNil(dynamicDataTableMeta?.dt_header_bg_palette_index) &&
        dynamicDataTableMeta.dt_header_bg_palette_index >= 0
      ) {
        obj.headerBackgroundColor = palette.value[dynamicDataTableMeta.dt_header_bg_palette_index]
      }
      if (
        !isNil(dynamicDataTableMeta?.dt_group_header_bg_palette_index) &&
        dynamicDataTableMeta.dt_group_header_bg_palette_index >= 0
      ) {
        obj.groupHeaderBackgroundColor = palette.value[dynamicDataTableMeta.dt_group_header_bg_palette_index]
      }
      return obj
    }
    return {}
  }

  function getDerivedStyleFromFeatureFlags(style: Record<string, any> | undefined): Record<string, any> | undefined {
    if (!launchDarkly.value.enable_theme_options_border_radius) {
      return omit(
        style,
        'borderTopLeftRadius',
        'borderTopRightRadius',
        'borderBottomLeftRadius',
        'borderBottomRightRadius',
      )
    }
    return style
  }

  function createStyleUpdate(
    updates: Partial<{
      backgroundColor?: string
      backgroundAttachment?: BackgroundAttachment
      backgroundImage?: string | null
      backgroundPositionX?: BackgroundPositionX
      backgroundPositionY?: BackgroundPositionY
      backgroundRepeat?: BackgroundRepeat
      backgroundSize?: BackgroundSize
      borderRadius?: ThemeBorderRadius
      height?: string | null
      left?: string
      marginBottom?: string | null
      padding?: ThemePadding
      position?: 'absolute' | 'relative' | 'static' | 'fixed'
      top?: string
      width?: string
    }> = {},
  ): Record<string, any> {
    const currentStyle = componentNode?.value?.style || {}

    const padding = updates.padding
      ? {
          paddingBottom: `${updates.padding.bottom}px`,
          paddingLeft: `${updates.padding.left}px`,
          paddingRight: `${updates.padding.right}px`,
          paddingTop: `${updates.padding.top}px`,
        }
      : {
          paddingBottom: currentStyle.paddingBottom,
          paddingLeft: currentStyle.paddingLeft,
          paddingRight: currentStyle.paddingRight,
          paddingTop: currentStyle.paddingTop,
        }

    const borderRadius = updates.borderRadius
      ? {
          borderTopLeftRadius: `${updates.borderRadius.topLeft}px`,
          borderTopRightRadius: `${updates.borderRadius.topRight}px`,
          borderBottomRightRadius: `${updates.borderRadius.bottomRight}px`,
          borderBottomLeftRadius: `${updates.borderRadius.bottomLeft}px`,
        }
      : {
          borderTopLeftRadius: currentStyle.borderTopLeftRadius,
          borderTopRightRadius: currentStyle.borderTopRightRadius,
          borderBottomRightRadius: currentStyle.borderBottomRightRadius,
          borderBottomLeftRadius: currentStyle.borderBottomLeftRadius,
        }

    const backgroundImage =
      updates.backgroundImage === null
        ? undefined // Clear the background image
        : updates.backgroundImage
          ? `url(${updates.backgroundImage})`
          : currentStyle.backgroundImage

    const backgroundColor =
      updates.backgroundColor === null
        ? undefined // Clear the background color
        : updates.backgroundColor
          ? updates.backgroundColor
          : currentStyle.backgroundColor

    const height =
      updates.height === null
        ? undefined // Clear the height
        : updates.height
          ? updates.height
          : currentStyle.height

    const marginBottom =
      updates.marginBottom === null
        ? undefined // Clear the margin bottom
        : updates.marginBottom
          ? updates.marginBottom
          : currentStyle.marginBottom

    return {
      backgroundAttachment: updates.backgroundAttachment ?? currentStyle.backgroundAttachment,
      backgroundColor,
      backgroundImage,
      backgroundPositionX: updates.backgroundPositionX ?? currentStyle.backgroundPositionX ?? 'center',
      backgroundPositionY: updates.backgroundPositionY ?? currentStyle.backgroundPositionY ?? 'center',
      backgroundRepeat: updates.backgroundRepeat ?? currentStyle.backgroundRepeat ?? 'no-repeat',
      backgroundSize: updates.backgroundSize ?? currentStyle.backgroundSize ?? 'auto',
      ...borderRadius,
      height,
      left: updates.left ?? currentStyle.left,
      marginBottom,
      ...padding,
      position: updates.position ?? currentStyle.position,
      top: updates.top ?? currentStyle.top,
      width: updates.width ?? currentStyle.width,
    }
  }

  return {
    borderRadiusThemeValues,
    componentSpacingThemeValues,
    paddingThemeValues,
    createStyleUpdate,
    getComponentSpecificThemeMetaOverrides,
    getCssThemeMetaStyleOverrides,
    getDerivedStyleFromFeatureFlags,
  }
}
